import { useCallback, useMemo } from "react";
import * as Yup from "yup";

import { regexForName } from "common/constant";

import { useApplicantTypeQuery } from "apis/ApplicantType";
import { getConsumerNumberValidation } from "utils/getConsumerNumberValidation";
import { getDocumentsValidationSchema } from "utils/getDocumentsValidationSchema";
import { proofOfOwnershipValidation } from "utils/proofOfOwnershipValidation";

export const useNameTransferValidationSchema = () => {
  const { data: { data: { items: applicantType = [] } = {} } = {} } =
    useApplicantTypeQuery();

  const validationSchema = useMemo(
    () => [
      Yup.object({
        utilityConsumerId: Yup.string().required("Customer number is required"),
        applicant: Yup.object({
          applicantTypeId: Yup.string().required("Applicant Type is required"),
          applicantName: Yup.string()
            .matches(regexForName, "Please enter a valid name")
            .required("Applicant Name is required"),
          panNumber: Yup.string()
            .matches(
              /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
              "Please enter a valid Pan number"
            )
            .when("applicantTypeId", {
              is: applicantType
                ?.find(
                  (application) =>
                    application.name.toLowerCase() === "individual"
                )
                ?.id.toString(),
              then: (schema) => schema,
              otherwise: (schema) => schema.required("Pan number is required"),
            })
            .nullable(),
          isGSTRegistered: Yup.string().required("GST is required"),
          gstNumber: Yup.string()
            .test("isGstRequired", "", function (_, context) {
              const [{ value: { isGSTRegistered, gstNumber } } = {}] =
                context.from || [];
              if (isGSTRegistered && !gstNumber) {
                return context.createError({
                  message: "GST number is required",
                });
              }
              return true;
            })
            .matches(
              /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
              "Please enter a valid GST number"
            ),
          occupancyTypeId: Yup.string().required("Occupancy type is required"),
          addressType: Yup.string().required("Address type is required"),
          city: Yup.object({
            id: Yup.number().required("City is required"),
          }),
          correspondenceAddress: Yup.string()
            .matches(/^[\w]+[\w\s.,-]*[\w\s.,-]+/, "Please enter valid address")
            .required("Correspondence Address is required"),
          pinCode: Yup.string()
            .matches(/^\d{6}$/, "Please enter valid pincode")
            .required("Pincode is required"),
          email: Yup.string()
            .email("Please enter valid email")
            .required("Email is required"),
          mobileNumber: Yup.string()
            .matches(/^\d{10}$/, "Please enter valid mobile number")
            .required("Mobile number is required"),
          alternateMobileNumber: Yup.string()
            .matches(/^\d{10}$/, "Please enter valid mobile number")
            .test(
              "checkIsMobileEqual",
              "Alternate mobile number can't be same as mobile number.",
              function () {
                const [{ value: current = null } = {}] = this.from || [];
                return +current.mobileNumber !== current.alternateMobileNumber;
              }
            ),
          alternateEmail: Yup.array().test(
            "isValidAlternateEmail",
            "",
            function () {
              const [{ value: current = null } = {}] = this.from || [];

              if (
                !!current.alternateEmail.length &&
                !current.alternateEmail.every((email) =>
                  email?.match(
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                  )
                )
              ) {
                return this.createError({
                  message: "Enter valid emails",
                });
              }
              if (
                current.alternateEmail.some((email) => email === current.email)
              ) {
                return this.createError({
                  message: "Alternate email can't be same as email.",
                });
              }
              return true;
            }
          ),
          landLine: Yup.string().matches(
            /^\d{8,11}$/,
            "Please enter a valid landline number"
          ),
        }),
      }),
      Yup.object({
        authorizedPerson: Yup.object({
          salutation: Yup.string().required("Salutation is required"),
          firstName: Yup.string()
            .matches(regexForName, "Please enter a valid first name")
            .required("First name is required"),
          middleName: Yup.string().matches(
            regexForName,
            "Please enter a valid middle name"
          ),
          lastName: Yup.string()
            .matches(regexForName, "Please enter a valid last name")
            .required("Last name is required"),
          email: Yup.string()
            .email("Please enter valid email")
            .required("Email is required"),
          mobileNumber: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Please enter valid mobile number"),
          landLine: Yup.string().matches(
            /^\d{8,11}$/,
            "Please enter a valid landline number"
          ),
          imageUrl: Yup.string().test(
            "imageRequired",
            "Please upload image",
            function () {
              const [{ value: current = null } = {}] = this.from || [];
              return !!(current?.image || current?.imageUrl);
            }
          ),
        }),
      }),
      Yup.object({
        documents: getDocumentsValidationSchema(),
        parentId: Yup.string().required("Reason of name transfer is required"),
        proofOfOwnershipDescription: proofOfOwnershipValidation,
      }),
      Yup.object({
        declaration: Yup.object({
          isConsumerDuesOutStanding: Yup.boolean(),
          consumerDuesOutstanding: Yup.string().when(
            "isConsumerDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isPremisesDuesOutstanding: Yup.boolean(),
          premisesDuesOutstanding: Yup.string().when(
            "isPremisesDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isFirmDuesOutstanding: Yup.boolean(),
          firmDuesOutstanding: Yup.string().when("isFirmDuesOutstanding", {
            is: true,
            then: (schema) => getConsumerNumberValidation(schema),
            otherwise: (schema) => schema,
          }),
        }),
      }),
    ],
    [applicantType]
  );

  const getValidationSchema = useCallback(
    (index) => validationSchema[index],
    [validationSchema]
  );
  return getValidationSchema;
};
